import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaCreditCard, FaSearch, FaUserMd, FaCalendarAlt, FaReceipt, FaPaperclip, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp, useAutoRefresh, triggerRefresh } from '../context/AppContext';
import FileAttachment from '../components/FileAttachment';
import RecordCard from '../components/RecordCard';
import { getBillings, createBilling, updateBilling, deleteBilling, getPatients, getDoctors, createBillingOrder, verifyBillingPayment, getFileUrl, MAX_ATTACHMENT_SIZE, MAX_ATTACHMENT_SIZE_MESSAGE } from '../services/api';

const loadRazorpay = () =>
  new Promise(resolve => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const EMPTY_SERVICE = { name: '', amount: '' };
const EMPTY_FORM = { patientName: '', patientId: '', doctorId: '', services: [{ ...EMPTY_SERVICE }], status: 'Pending', notes: '', date: new Date().toISOString().split('T')[0] };

const Billing = () => {
  const { showToast, user } = useApp();
  const role = user?.role || 'Doctor';
  const canCreateInvoiceAllowed = user?.canCreateInvoice !== false;
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const fileRef = useRef();

  const handlePayNow = async (item) => {
    const id = item._id || item.id;
    if (payingId) return;
    setPayingId(id);
    try {
      const orderRes = await createBillingOrder(id);
      const { orderId, amount, keyId } = orderRes.data.data;

      const loaded = await loadRazorpay();
      if (!loaded) { showToast('Payment gateway failed to load', 'error'); setPayingId(null); return; }

      const options = {
        key:         keyId,
        amount,
        currency:    'INR',
        name:        'DocApps Solutions',
        description: item.patientName ? `Invoice — ${item.patientName}` : 'Billing Payment',
        order_id:    orderId,
        prefill:     { name: user?.name || item.patientName, email: user?.email || '' },
        theme:       { color: '#E8500A' },
        handler: async (response) => {
          try {
            const res = await verifyBillingPayment(id, {
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            });
            const updated = res.data?.data;
            setItems(prev => prev.map(i => (i._id || i.id) === id ? (updated || i) : i));
            setViewItem(prev => (prev && (prev._id || prev.id) === id) ? (updated || prev) : prev);
            showToast('Payment successful! Invoice emailed to you.', 'success');
          } catch (err) {
            showToast(err.message || 'Payment verification failed. Contact support.', 'error');
          }
          setPayingId(null);
        },
        modal: { ondismiss: () => { showToast('Payment cancelled', 'error'); setPayingId(null); } },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => { showToast('Payment failed. Please retry.', 'error'); setPayingId(null); });
      rzp.open();
    } catch (err) {
      showToast(err.message || 'Failed to start payment', 'error');
      setPayingId(null);
    }
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const [blRes, patRes, docRes] = await Promise.allSettled([
        getBillings(),
        role !== 'Patient' ? getPatients() : Promise.resolve({ data: { data: [] } }),
        role !== 'Doctor'  ? getDoctors() : Promise.resolve({ data: { data: [] } }),
      ]);
      setItems(blRes.status === 'fulfilled' ? (blRes.value.data?.data || []) : []);
      if (patRes.status === 'fulfilled') setPatients(patRes.value.data?.data || []);
      if (docRes.status === 'fulfilled') setDoctors(docRes.value.data?.data || []);
    } catch { setItems([]); }
    setLoading(false);
  }, [role]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useAutoRefresh(fetchItems, 'billing');

  const getPatientPhoto = (item) => {
    if (role === 'Patient') return getFileUrl(user?.profileImage) || null;
    const p = patients.find(pt => String(pt.userId) === String(item.patientId) || String(pt.id) === String(item.patientId));
    return getFileUrl(p?.account?.profileImage) || null;
  };

  // A doctor viewing their own Billing page never fetches the full doctor
  // list (skipped above for role === 'Doctor') — every row here is their own
  // billing record, so doctorId is always their own id. Resolve it directly
  // instead of falling through to the "Dr. #4" id fallback.
  const getDoctorName = (doctorId) => {
    if (!doctorId) return null;
    if (role === 'Doctor' && doctorId === user?.id) return user.name;
    return doctors.find(d => d.id === doctorId)?.name || `Dr. #${doctorId}`;
  };

  // Admin: once a Doctor is picked, only show patients Admin has assigned to that doctor.
  const visiblePatients = (role === 'Admin' && form.doctorId)
    ? patients.filter(p => String(p.doctorId) === String(form.doctorId))
    : patients;

  const addService = () => setForm(p => ({ ...p, services: [...p.services, { ...EMPTY_SERVICE }] }));
  const removeService = (i) => setForm(p => ({ ...p, services: p.services.filter((_, idx) => idx !== i) }));
  const updateService = (i, field, val) => setForm(p => ({ ...p, services: p.services.map((s, idx) => idx === i ? { ...s, [field]: val } : s) }));

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('patientName', form.patientName || '');
    fd.append('patientId', form.patientId || '');
    fd.append('doctorId', form.doctorId || '');
    fd.append('date', form.date || '');
    fd.append('status', form.status || 'Pending');
    fd.append('notes', form.notes || '');
    fd.append('services', JSON.stringify(form.services));
    if (editId) fd.append('existingAttachments', JSON.stringify(existingAttachments));
    selectedFiles.forEach(f => fd.append('files', f));
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = (selectedFiles.length > 0 || editId) ? buildFormData() : form;
      if (editId) {
        const res = await updateBilling(editId, payload);
        setItems(prev => prev.map(i => (i.id === editId || i._id === editId) ? (res.data?.data || { ...i, ...form }) : i));
        showToast('Billing updated!', 'success');
      } else {
        const res = await createBilling(payload);
        const created = res.data?.data || res.data;
        setItems(prev => [created, ...prev]);
        showToast(
          created?.approvalStatus === 'pending'
            ? 'Invoice submitted — waiting for Admin approval before the patient sees it.'
            : 'Billing record created!',
          'success'
        );
        triggerRefresh('billing');
      }
      setShowModal(false); setEditId(null); setSelectedFiles([]); setExistingAttachments([]);
      setForm({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] });
    } catch (err) {
      showToast(err.message || 'Failed to save', 'error');
    }
    setSaving(false);
  };

  const handleView = (item) => {
    setViewItem(item);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this billing record?')) return;
    try {
      await deleteBilling(id);
      setItems(prev => prev.filter(i => i._id !== id && i.id !== id));
      showToast('Billing record deleted', 'error');
    } catch (err) { showToast(err.message || 'Delete failed', 'error'); }
  };

  const filtered = items.filter(i => {
    if (search && !i.patientName?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterDate && i.date !== filterDate) return false;
    return true;
  });

  const stats = {
    total: items.reduce((sum, i) => sum + (parseFloat(i.totalAmount) || 0), 0),
    paid: items.filter(i => i.status === 'Paid').reduce((sum, i) => sum + (parseFloat(i.totalAmount) || 0), 0),
    pending: items.filter(i => i.status === 'Pending').reduce((sum, i) => sum + (parseFloat(i.totalAmount) || 0), 0),
  };

  if (showModal) {
    return (
      <div className="page-wrapper">
        <Header title={editId ? 'Edit Invoice' : 'New Invoice'} />
        <Sidebar />
        <main className="page-content">
          <div className="page-header">
            <div>
              <h1><FaCreditCard style={{ color: 'var(--primary)', marginRight: 8 }} />{editId ? 'Edit Invoice' : 'New Invoice'}</h1>
              <p>{editId ? 'Update billing details' : 'Create a new invoice'}</p>
            </div>
            <button className="btn btn-outline" onClick={() => { setShowModal(false); setEditId(null); setSelectedFiles([]); setExistingAttachments([]); setForm({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] }); }}>
              <FaArrowLeft /> Back
            </button>
          </div>
          <div className="card" style={{ padding: 24, maxWidth: 700, margin: '0 auto' }}>
            <form onSubmit={handleSubmit}>
              {role === 'Admin' && doctors.length > 0 && (
                <div className="form-group">
                  <label>Doctor</label>
                  <select value={form.doctorId} onChange={e => {
                    const doctorId = e.target.value;
                    setForm(prev => {
                      // Clear the previously picked patient if they aren't assigned to the new doctor.
                      const stillValid = !doctorId || patients.some(pt =>
                        String(pt.doctorId) === String(doctorId) && String(pt.userId || pt.id) === String(prev.patientId)
                      );
                      return stillValid ? { ...prev, doctorId } : { ...prev, doctorId, patientId: '', patientName: '' };
                    });
                  }}>
                    <option value="">Select doctor...</option>
                    {doctors.map(d => <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ''}</option>)}
                  </select>
                </div>
              )}
              <div className="form-grid">
                <div className="form-group">
                  <label>Patient *</label>
                  <select required value={form.patientId} onChange={e => {
                    const pObj = visiblePatients.find(pt => String(pt.userId) === e.target.value || String(pt.id) === e.target.value);
                    const pid = pObj?.userId ? String(pObj.userId) : e.target.value;
                    setForm(prev => ({ ...prev, patientId: pid, patientName: pObj ? pObj.name : '' }));
                  }}>
                    <option value="">Select patient...</option>
                    {visiblePatients.map(p => <option key={p.id} value={p.userId || p.id}>{p.name} {p.userId ? '(Registered)' : ''}</option>)}
                  </select>
                  {role === 'Admin' && form.doctorId && visiblePatients.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>No patients assigned to this doctor yet.</div>
                  )}
                </div>
                <div className="form-group"><label>Date</label><input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} /></div>
              </div>
              <div className="section-title">Services</div>
              {form.services.map((svc, i) => (
                <div key={i} style={{ background: 'var(--primary-bg)', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                  <div className="form-grid" style={{ marginBottom: 0 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Description *</label>
                      <input required value={svc.name} onChange={e => updateService(i, 'name', e.target.value)} placeholder="e.g. Consultation Fee" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Amount (₹) *</label>
                      <input required type="number" min="0" value={svc.amount} onChange={e => updateService(i, 'amount', e.target.value)} placeholder="0.00" />
                    </div>
                  </div>
                  {form.services.length > 1 && (
                    <button type="button" onClick={() => removeService(i)} className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12, marginTop: 8 }}>Remove</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addService} className="btn btn-outline" style={{ width: '100%', marginBottom: 12 }}>
                <FaPlus /> Add Another Service
              </button>
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                  <option>Pending</option><option>Paid</option><option>Cancelled</option>
                </select>
              </div>
              <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Payment details..." /></div>
              <div className="form-group">
                <label><FaPaperclip style={{ marginRight: 6 }} />Attachment (Invoice/Receipt)</label>
                {existingAttachments.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {existingAttachments.map((att, i) => (
                      <FileAttachment key={i} url={att.url} filename={att.name} compact={true}
                        onRemove={() => setExistingAttachments(prev => prev.filter((_, idx) => idx !== i))} />
                    ))}
                  </div>
                )}
                <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => {
                  const files = Array.from(e.target.files);
                  if (files.some(f => f.size > MAX_ATTACHMENT_SIZE)) {
                    showToast(MAX_ATTACHMENT_SIZE_MESSAGE, 'error');
                    e.target.value = '';
                    return;
                  }
                  setSelectedFiles(files);
                }} style={{ padding: '8px 0', fontSize: 13 }} />
                {selectedFiles.length > 0 && <div style={{ fontSize: 12, color: 'var(--primary)', marginTop: 4 }}>{selectedFiles.map(f => f.name).join(', ')}</div>}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => { setShowModal(false); setEditId(null); setSelectedFiles([]); setExistingAttachments([]); setForm({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] }); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <Header title="Billing" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div>
            <h1><FaCreditCard style={{ color: 'var(--primary)', marginRight: 8 }} />Billing & Payments</h1>
            <p>{items.length} records found</p>
          </div>
          {role === 'Admin' && (
            <button className="btn btn-primary" onClick={() => { setEditId(null); setSelectedFiles([]); setExistingAttachments([]); setForm({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] }); setShowModal(true); }}>
              <FaPlus /> New Invoice
            </button>
          )}
          {role === 'Doctor' && (
            canCreateInvoiceAllowed ? (
              <button className="btn btn-primary" onClick={() => { setEditId(null); setSelectedFiles([]); setExistingAttachments([]); setForm({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] }); setShowModal(true); }}>
                <FaPlus /> New Invoice
              </button>
            ) : (
              <span style={{ fontSize: 12, color: 'var(--text-light)', fontStyle: 'italic' }}>Invoice creation disabled by Admin</span>
            )
          )}
        </div>

        {role !== 'Patient' && items.length > 0 && (
          <div className="billing-summary-row">
            <div className="billing-stat"><div className="billing-stat-val">₹{stats.total.toLocaleString()}</div><div className="billing-stat-label">TOTAL BILLED</div></div>
            <div className="billing-stat"><div className="billing-stat-val" style={{ color: '#2e7d32' }}>₹{stats.paid.toLocaleString()}</div><div className="billing-stat-label">COLLECTED</div></div>
            <div className="billing-stat"><div className="billing-stat-val" style={{ color: '#c62828' }}>₹{stats.pending.toLocaleString()}</div><div className="billing-stat-label">PENDING</div></div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="search-bar" style={{ flex: 1, minWidth: 180, marginBottom: 0 }}>
            <FaSearch />
            <input placeholder="Search invoices by patient name..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <input
            type="date"
            value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
            title="Filter by date"
          />
          {filterDate && (
            <button className="btn btn-outline" style={{ padding: '8px 12px', fontSize: 12 }} onClick={() => setFilterDate('')}>
              Clear Filter
            </button>
          )}
        </div>

        {loading ? <div className="loading-container"><div className="spinner" /></div> :
          filtered.length === 0 ? <div className="empty-state"><FaCreditCard className="empty-state-icon" /><h3>No billing records</h3></div> :
          <div className="record-grid">
            {filtered.map(item => {
              const svcs = item.services || item.BillingServices || [];
              const doctorName = getDoctorName(item.doctorId);
              const meta = [{ icon: <FaCalendarAlt size={11} />, text: item.date ? new Date(item.date).toLocaleDateString('en-IN') : '-' }];
              if (doctorName) meta.push({ icon: <FaUserMd size={11} />, text: doctorName });
              const hasAttachments = (Array.isArray(item.attachments) && item.attachments.length > 0) || item.file_url;
              return (
                <RecordCard
                  key={item._id || item.id}
                  avatarLetter={(item.patientName || '?')[0].toUpperCase()}
                  avatarImage={getPatientPhoto(item)}
                  title={item.patientName}
                  meta={meta}
                  subLabel={<>
                    Total: <strong>₹{(parseFloat(item.totalAmount) || 0).toLocaleString()}</strong> · <span className={`status-badge status-${item.status?.toLowerCase()}`}>{item.status}</span>
                    {item.approvalStatus === 'pending' && <> · <span className="status-badge" style={{ background: '#fff3e0', color: '#e65100' }}>Awaiting Admin Approval</span></>}
                    {item.approvalStatus === 'rejected' && <> · <span className="status-badge" style={{ background: '#ffebee', color: '#c62828' }}>Rejected by Admin</span></>}
                  </>}
                  items={svcs.map(s => ({
                    icon: <FaReceipt size={13} />,
                    name: s.name,
                    badges: [`₹${s.amount}`],
                  }))}
                  note={item.notes}
                  onView={() => handleView(item)}
                  actions={(role === 'Doctor' || role === 'Admin') && (
                    <>
                      <button className="action-btn action-btn-edit" onClick={() => {
                        setForm({
                          patientName: item.patientName,
                          patientId: item.patientId || '',
                          doctorId: item.doctorId || '',
                          services: svcs.length ? svcs.map(s => ({ name: s.name, amount: s.amount })) : [{ ...EMPTY_SERVICE }],
                          status: item.status,
                          notes: item.notes || '',
                          date: item.date
                        });
                        setSelectedFiles([]);
                        setExistingAttachments(Array.isArray(item.attachments) && item.attachments.length > 0 ? item.attachments : (item.file_url ? [{ url: item.file_url, name: null }] : []));
                        setEditId(item._id || item.id); setShowModal(true);
                      }}><FaEdit /></button>
                      <button className="action-btn action-btn-delete" onClick={() => handleDelete(item._id || item.id)}><FaTrash /></button>
                    </>
                  )}
                >
                  {hasAttachments && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                      {Array.isArray(item.attachments) && item.attachments.map((att, i) => (
                        <FileAttachment key={i} url={att.url} filename={att.name} compact={true} />
                      ))}
                      {(!Array.isArray(item.attachments) || item.attachments.length === 0) && item.file_url && (
                        <FileAttachment url={item.file_url} compact={true} />
                      )}
                    </div>
                  )}
                  {role === 'Patient' && item.status !== 'Paid' && (
                    <button
                      className="btn btn-primary"
                      style={{ width: '100%', marginTop: 10 }}
                      disabled={payingId === (item._id || item.id)}
                      onClick={() => handlePayNow(item)}
                    >
                      <FaCreditCard style={{ marginRight: 6 }} />
                      {payingId === (item._id || item.id) ? 'Processing...' : 'Pay Now'}
                    </button>
                  )}
                  {role === 'Patient' && item.status === 'Paid' && (
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#2e7d32', fontWeight: 700, fontSize: 13 }}>
                        <FaCheckCircle /> Payment Successful
                      </span>
                      {item.invoiceNumber && (
                        <FileAttachment url={`/api/billing/${item._id || item.id}/invoice`} filename={`${item.invoiceNumber}.pdf`} compact={true} />
                      )}
                    </div>
                  )}
                </RecordCard>
              );
            })}
          </div>
        }
      </main>
      <BottomNav />

      {showViewModal && viewItem && (
        <div className="side-panel-overlay" onClick={e => e.target === e.currentTarget && setShowViewModal(false)}>
          <div className="side-panel">
            <div className="modal-header">
              <h2>Invoice Details</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, paddingBottom: 10, borderBottom: '1px solid #eee' }}>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Billed To</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>{viewItem.patientName}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Date & Status</div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{viewItem.date ? new Date(viewItem.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</div>
                  <span className={`status-badge status-${viewItem.status?.toLowerCase()}`}>{viewItem.status}</span>
                </div>
              </div>

              <div style={{ marginBottom: 15 }}>
                <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Services Rendered</div>
                <div style={{ border: '1px solid #eee', borderRadius: 10, overflow: 'hidden' }}>
                  {(viewItem.services || viewItem.BillingServices || []).map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 15px', borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                      <span style={{ fontSize: 14 }}>{s.name}</span>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>₹{s.amount}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 15px', background: '#e3f2fd', color: '#1976d2', fontWeight: 800, fontSize: 16 }}>
                    <span>Total Amount</span>
                    <span>₹{(parseFloat(viewItem.totalAmount) || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {viewItem.notes && (
                <div style={{ marginBottom: 15 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>Notes</div>
                  <div style={{ background: '#fff9c4', padding: 10, borderRadius: 8, fontSize: 14, color: '#5d4037' }}>{viewItem.notes}</div>
                </div>
              )}

              {((Array.isArray(viewItem.attachments) && viewItem.attachments.length > 0) || viewItem.file_url) && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Attachments</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {Array.isArray(viewItem.attachments) && viewItem.attachments.map((att, i) => (
                      <FileAttachment key={i} url={att.url} filename={att.name} />
                    ))}
                    {!Array.isArray(viewItem.attachments) && viewItem.file_url && <FileAttachment url={viewItem.file_url} />}
                  </div>
                </div>
              )}

              {role === 'Patient' && viewItem.status === 'Paid' && (
                <div style={{ marginTop: 15, background: '#e8f5e9', borderRadius: 10, padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#2e7d32', fontWeight: 700, marginBottom: viewItem.invoiceNumber ? 8 : 0 }}>
                    <FaCheckCircle /> Payment Successful
                  </div>
                  {viewItem.invoiceNumber && (
                    <FileAttachment url={`/api/billing/${viewItem._id || viewItem.id}/invoice`} filename={`${viewItem.invoiceNumber}.pdf`} />
                  )}
                </div>
              )}
            </div>
            {role === 'Patient' && viewItem.status !== 'Paid' && (
              <button
                className="btn btn-primary"
                style={{ width: '100%', marginBottom: 10 }}
                disabled={payingId === (viewItem._id || viewItem.id)}
                onClick={() => handlePayNow(viewItem)}
              >
                <FaCreditCard style={{ marginRight: 6 }} />
                {payingId === (viewItem._id || viewItem.id) ? 'Processing...' : 'Pay Now'}
              </button>
            )}
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowViewModal(false)}>Close</button>
          </div>
        </div>
      )}

      <style>{`
        .billing-summary-row { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; margin-bottom: 16px; }
        .billing-stat { background: white; border-radius: 12px; padding: 14px; text-align: center; box-shadow: var(--shadow); }
        .billing-stat-val { font-size: 20px; font-weight: 800; color: var(--primary); }
        .billing-stat-label { font-size: 11px; color: var(--text-light); margin-top: 2px; font-weight: 600; }
      `}</style>
    </div>
  );
};

export default Billing;
